import React from "react";
import { LocaSmartLayout } from "../../components/locasmart-layout";
import {
    ExternalLink,
    Smartphone,
    FileText,
    Droplets,
    Camera,
    CreditCard,
    LucideIcon
} from "lucide-react";

/* =========================
   Tipagem
========================= */
interface ItemProps {
    icon: LucideIcon;
    text: string;
}

/* =========================
   Componente auxiliar
========================= */
function Item({ icon: Icon, text }: ItemProps) {
    return (
        <div className="flex items-center space-x-3 text-white">
            <div className="bg-white/10 p-1.5 rounded-lg">
                <Icon size={20} />
            </div>
            <span className="font-medium">{text}</span>
        </div>
    );
}

/* =========================
   View principal
========================= */
function AppLocatarioView() {
    const androidUrl =
        "https://play.google.com/store/apps/details?id=com.locasmartpro.gestaodelocadoras";
    
    const iosUrl = 
        "https://apps.apple.com/br/app/locasmartpro-app-locat%C3%A1rio/id6757689416";

    return (
        <LocaSmartLayout>
            <style>
                {`
                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .animate-fade-in-up {
                        animation: fadeInUp 0.6s ease-out forwards;
                    }
                `}
            </style>

            <div className="flex items-center justify-center w-full min-h-[calc(100vh-100px)] bg-gray-50 p-6">
                <div className="max-w-4xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row animate-fade-in-up border border-gray-100">

                    {/* ================= LEFT ================= */}
                    <div className="md:w-1/2 p-10 flex flex-col justify-center bg-gradient-to-br from-indigo-700 to-indigo-900 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2" />
                        <div className="absolute bottom-0 right-0 w-48 h-48 bg-white opacity-10 rounded-full translate-x-1/3 translate-y-1/3" />

                        <div className="relative z-10">
                            <div className="bg-white/20 w-16 h-16 rounded-2xl flex items-center justify-center backdrop-blur-sm mb-6 shadow-lg border border-white/10">
                                <Smartphone size={32} className="text-white" />
                            </div>

                            <h1 className="text-4xl font-bold text-white mb-4 drop-shadow-md">
                                APP do Locatário
                            </h1>

                            <p className="text-indigo-100 text-lg leading-relaxed mb-8 font-medium">
                                Aplicativo integrado ao LocaSmartPro que permite ao
                                locatário acompanhar pagamentos, contratos, vistorias
                                e manutenções de forma simples e rápida.
                            </p>

                            <div className="space-y-4">
                                <Item
                                    icon={CreditCard}
                                    text="Histórico de pagamentos e PIX copia e cola"
                                />
                                <Item
                                    icon={FileText}
                                    text="Visualização do contrato"
                                />
                                <Item
                                    icon={Camera}
                                    text="Envio de vistorias pelo app"
                                />
                                <Item
                                    icon={Droplets}
                                    text="Registro de trocas de óleo"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ================= RIGHT ================= */}
                    <div className="md:w-1/2 p-10 flex flex-col justify-center items-center bg-white text-center">
                        <div className="mb-8 relative">
                            <div className="absolute inset-0 bg-indigo-100 rounded-full blur-xl opacity-50 animate-pulse" />
                            <img
                                src="https://cdn-icons-png.flaticon.com/512/888/888857.png"
                                alt="Smartphone App"
                                className="w-32 h-32 object-contain relative z-10 drop-shadow-xl animate-bounce"
                                style={{ animationDuration: "3s" }}
                            />
                        </div>

                        <h2 className="text-3xl font-bold text-gray-900 mb-3">
                            Baixe agora o APP
                        </h2>

                        <p className="text-gray-600 mb-8 max-w-sm mx-auto">
                            O acesso do locatário é criado no sistema em:
                            <br />
                            <strong>
                                Locatários → 3 pontinhos → Login no APP
                            </strong>
                        </p>

                        <div className="flex flex-col gap-4 w-full md:w-auto">
                            {/* Botão Android */}
                            <a
                                href={androidUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-indigo-600 rounded-xl hover:bg-indigo-700 hover:shadow-xl hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600 w-full overflow-hidden"
                            >
                                <span className="relative z-10 flex items-center">
                                    Baixar na Play Store
                                    <ExternalLink
                                        size={18}
                                        className="ml-2 transition-transform group-hover:translate-x-1"
                                    />
                                </span>
                                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-500" />
                            </a>

                            {/* Botão iOS */}
                            <a
                                href={iosUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-gray-800 rounded-xl hover:bg-gray-900 hover:shadow-xl hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 w-full overflow-hidden"
                            >
                                <span className="relative z-10 flex items-center">
                                    Baixar na App Store
                                    <ExternalLink
                                        size={18}
                                        className="ml-2 transition-transform group-hover:translate-x-1"
                                    />
                                </span>
                                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-500" />
                            </a>
                        </div>

                        <p className="mt-6 text-sm text-gray-400 font-medium">
                            Disponível para Android e iOS • Integrado ao sistema
                        </p>
                    </div>
                </div>
            </div>
        </LocaSmartLayout>
    );
}

export default AppLocatarioView;